import ExerciseSupport from '../../../student/components/exercises/ExerciseSupport';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import React from 'react';
import { shows, SpeakingExercise } from '../../../student/mockLessonContent';
import { BLUE, cardSx, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SURFACE_SOFT, WARNING_TEXT, YELLOW } from '../../../student/studentStyles';
import AudioPlayButton from '../../../student/components/AudioPlayButton';
import RecordAnswerPanel from '../../../student/components/exercises/RecordAnswerPanel';
import SpeakingHero from '../../../student/components/exercises/SpeakingHero';
import TeacherNote from '../../../student/components/exercises/TeacherNote';
import { ExerciseReady, useExerciseLayout } from './exerciseLayout';

interface SpeakingExerciseCardProps {
    exercise: SpeakingExercise;
    onReady: ExerciseReady;
}

const sectionLabelSx = { fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SLATE, mb: 1 };

// "Prompt, Speak With Grade" and "Prompt, Speak Anything, No Grade". The answer is
// never shown up front: the student hears the teacher's question (required), sees
// their cue, then answers in their own words.
//  Graded: one recording is matched to the closest accepted answer and scored on
//  the legacy pass gate. The hint and starters appear after a failed take; after
//  two failed takes an accepted answer is shown so the student knows what to say.
//  No grade: any recording completes; ideas are available on request.
export default function SpeakingExerciseCard({ exercise, onReady }: SpeakingExerciseCardProps) {
    const desktop = useExerciseLayout() === 'desktop';
    const [heardQuestion, setHeardQuestion] = React.useState(false);
    const [failedTakes, setFailedTakes] = React.useState(0);
    const [ideasOpen, setIdeasOpen] = React.useState(false);
    // The mock always matches the first accepted answer.
    const matched = exercise.accepted[0];
    const showSupport = exercise.graded ? failedTakes >= 1 : ideasOpen;

    const support = (
        <>
            {exercise.hint && (
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 2, p: 1.75, borderRadius: '14px', backgroundColor: `${YELLOW}26` }}>
                    <LightbulbRoundedIcon sx={{ color: WARNING_TEXT, fontSize: 20, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: NAVY }}>{exercise.hint}</Typography>
                </Stack>
            )}
            {shows(exercise, 'starters') && exercise.starters.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography sx={sectionLabelSx}>RESPONSE STARTERS</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {exercise.starters.map(starter => (
                            <Chip key={starter} label={starter} sx={{ fontFamily: POPPINS, color: BLUE, backgroundColor: `${BLUE}14` }} />
                        ))}
                    </Stack>
                </Box>
            )}
        </>
    );

    return (
        <Box sx={{ ...cardSx, overflow: 'hidden' }}>
            <SpeakingHero
                label={exercise.graded ? 'SPEAKING PRACTICE' : 'FREE SPEAKING · NOT SCORED'}
                hanzi={exercise.heroHanzi}
                pinyin={shows(exercise, 'pinyin') ? exercise.heroPinyin : undefined}
            />

            <Box sx={{ p: desktop ? 3 : 2.5 }}>
                {exercise.teacher?.intro && <Box sx={{ mb: 2.5 }}><TeacherNote text={exercise.teacher.intro} /></Box>}
                <Box sx={{ display: desktop ? 'grid' : 'block', gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, .9fr)', gap: 3, alignItems: 'start' }}>
                    <Box>
                        {/* Step 1: the teacher's question — must be heard before answering. */}
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{
                                width: 42, height: 42, borderRadius: '14px', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${BLUE}1A`,
                            }}>
                                <VolumeUpRoundedIcon sx={{ color: BLUE }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>
                                    {exercise.speaker.nameHanzi}
                                </Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE }}>
                                    {heardQuestion ? exercise.speaker.nameEnglish : 'Listen to the question first'}
                                </Typography>
                            </Box>
                            <AudioPlayButton ariaLabel="Play the question" size={40} required onPlayed={() => setHeardQuestion(true)} />
                        </Stack>

                        <Box sx={{ mt: 2.5, p: 2.25, borderRadius: '16px', backgroundColor: SURFACE_SOFT }}>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: 20, color: NAVY, lineHeight: 1.5 }}>
                                {exercise.question.hanzi}
                            </Typography>
                            {shows(exercise, 'english') && exercise.question.english && (
                                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.75 }}>
                                    {exercise.question.english}
                                </Typography>
                            )}
                        </Box>

                        {/* Step 2: the student's cue (legacy student_response_prompt), revealed after the question. */}
                        {heardQuestion && exercise.studentCue && (
                            <Box sx={{ mt: 2, p: 2, borderRadius: '16px', border: `1px dashed ${RING_TRACK}` }}>
                                <Typography sx={sectionLabelSx}>YOUR ANSWER</Typography>
                                <Typography sx={{ fontFamily: POPPINS, fontSize: 19, color: NAVY, letterSpacing: '0.04em' }}>
                                    {exercise.studentCue}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Box sx={desktop ? { pl: 3, borderLeft: `1px solid ${RING_TRACK}` } : { mt: 1 }}>
                        {desktop && (
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: SLATE }}>
                                YOUR RESPONSE
                            </Typography>
                        )}

                        {!exercise.graded && (exercise.hint || shows(exercise, 'starters') && exercise.starters.length > 0) && !ideasOpen && (
                            <Button
                                startIcon={<TipsAndUpdatesRoundedIcon />}
                                onClick={() => setIdeasOpen(true)}
                                sx={{ minHeight: 44, mt: desktop ? 1.5 : 2, fontFamily: INTER, fontWeight: 700, fontSize: 13, color: BLUE, textTransform: 'none', border: `1px solid ${RING_TRACK}`, borderRadius: '999px', px: 2 }}
                            >
                                Need ideas?
                            </Button>
                        )}
                        {showSupport && support}

                        {exercise.graded && failedTakes >= 2 && matched && (
                            <Box sx={{ mt: 2 }}>
                                <Typography sx={sectionLabelSx}>TRY SAYING</Typography>
                                <Box sx={{ p: 2, borderRadius: '16px', backgroundColor: SURFACE_SOFT, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: 18, color: NAVY }}>{matched.hanzi}</Typography>
                                        {shows(exercise, 'pinyin') && <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>{matched.pinyin}</Typography>}
                                    </Box>
                                    <AudioPlayButton ariaLabel="Play an accepted answer" size={40} />
                                </Box>
                            </Box>
                        )}

                        <RecordAnswerPanel
                            grades={matched?.grades ?? []}
                            takes={matched?.takes}
                            scoring={exercise.graded ? 'graded' : 'none'}
                            disabled={!heardQuestion}
                            disabledHint="Play the question first, then record your answer."
                            correctText={exercise.teacher?.spokenCorrect}
                            incorrectText={exercise.teacher?.spokenIncorrect}
                            matchedText={exercise.graded ? matched?.hanzi : undefined}
                            onTake={(_outcome, failed) => setFailedTakes(failed)}
                            onComplete={() => onReady('done')}
                            onSkippable={() => onReady('skip')}
                        />
                    </Box>
                </Box>
                <ExerciseSupport exercise={exercise} />
            </Box>
        </Box>
    );
}
